import { SYNC_FIELDS_STATUS } from "constants/opekepe";
import { isEn } from "i18next-config";
import moment from "moment";
import { fieldDimensionsNumber } from "utils/fieldDimensions";
import { getFieldOwner } from "utils/getFieldOwner";
import { userFullName } from "utils/namingUtils";

const getLatestEcoScheme = (field) => {
  if (!field) return;
  if (!Array.isArray(field.ecoschemes)) return;
  return field.ecoschemes[field.ecoschemes.length - 1];
};

const getLatestSyncDate = (applications, latestActiveYear) => {
  if (!Array.isArray(applications) || applications.length === 0) return null;

  const latestApplication = applications
    .filter((app) => app.status === SYNC_FIELDS_STATUS.SYNCED)
    .sort((a, b) => moment(b.dateSynced).diff(moment(a.dateSynced)))[0];

  // If the user has not synced for the active year, then we should not show the date of the last sync
  if (parseInt(latestActiveYear) > parseInt(latestApplication.year)) {
    return null;
  }

  if (latestApplication) {
    return moment(latestApplication.dateSynced).format("MMM DD YYYY, HH:mm");
  }

  return null;
};

export const transformFields = (fields, t) =>
  fields.map((field) => {
    return {
      ...field,
      bio: field?.isBioCrop
        ? t("KydTable.BioStatus.Bio")
        : t("KydTable.BioStatus.Conventional"),
      crop: field?.varietyRead?.[`cropName${isEn ? "En" : "Gr"}`],
      variety: field?.varietyRead?.[`varietyName${isEn ? "En" : "Gr"}`],
      area: field?.municipality?.[`prefectureName${isEn ? "En" : "Gr"}`],
      municipality: field?.municipality?.[`name${isEn ? "En" : "Gr"}`],
      municipalityId: `mun_${field?.municipality?.id}`,
      ecoschemes: getLatestEcoScheme(field)?.fieldEcoschemeSubsidyList?.map(
        (scheme) => scheme.ecoschemeSubsidyCode
      ),
      areaOfField: fieldDimensionsNumber(isEn, field?.areaOfField),
      standardPerformanceIndex: t("KydTable.N/A"),
    };
  });

export const populateFarmers = (myFarmers, associates, latestActiveYear) =>
  myFarmers?.map((farmer) => {
    const associate = associates?.find((as) => as?.email === farmer?.email);
    const ecoschemesArray = associate?.fields?.reduce((acc, field) => {
      const ecoschemes =
        getLatestEcoScheme(field)?.fieldEcoschemeSubsidyList?.map(
          (scheme) => scheme.ecoschemeSubsidyCode
        ) || [];
      return [...acc, ...ecoschemes];
    }, []);
    const ecoschemes = Array.from(new Set(ecoschemesArray));
    const bio = associate?.fields?.some((field) => field?.isBioCrop);
    const nonBio = associate?.fields?.some((field) => !field?.isBioCrop);
    const municipalities = Array.from(
      new Set(
        associate?.fields?.map((field) => `mun_${field?.municipality?.id}`)
      )
    );
    const varieties = Array.from(
      new Set(associate?.fields?.map((field) => field?.varietyRead?.varietyId))
    );
    const syncDate = getLatestSyncDate(
      farmer?.opekepeApplications,
      latestActiveYear
    );
    const totalAreaOfFields = fieldDimensionsNumber(
      isEn,
      farmer.billableStremmata * 1000
    );

    return {
      ...farmer,
      ecoschemes,
      bio,
      nonBio,
      fields: associate?.fields,
      fieldsCount: farmer.fieldsCount,
      syncDate,
      municipalities,
      varieties,
      totalAreaOfFields,
    };
  }) ?? [];

export const populateFields = (fields, myFarmers) => {
  const fieldsPopulated = fields?.map((field) => {
    const farmer = getFieldOwner(field);
    const farmerPopulated = myFarmers?.find(
      (myFarmer) => myFarmer?.email === farmer?.email
    );

    field.farmerFullName = userFullName({ user: farmerPopulated });

    let status = "registeredKydUser";
    if (field?.inputSource == 4 || field?.inputSource == 5) {
      status = "syncedFields";
    } else if (farmerPopulated?.opekepe) {
      status = "opekepe";
    } else if (farmerPopulated?.otp) {
      status = "otp";
    } else if (farmerPopulated?.camvioConnection) {
      status = "camvioConnection";
    }

    return { ...field, farmer: farmerPopulated, status };
  });
  return fieldsPopulated?.filter((field) => Boolean(field.farmer)) ?? [];
};
